import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Spin } from "@douyinfe/semi-ui";
import { ProtectedRoute } from "@/components/ProtectedRoute";

const LoginPage = lazy(() => import("./pages/Login/index"));
const PageHeadLayout = lazy(() => import("./layouts/PageHeadLayout/index"));
const UsersPage = lazy(() => import("./pages/Users/index"));
const CasesPage = lazy(() => import("./pages/Cases/index"));
const CaseDetailPage = lazy(() => import("./pages/Cases/Detail/index"));
const AssignmentsPage = lazy(() => import("./pages/Assignments/index"));
const LettersPage = lazy(() => import("./pages/Letters/index"));
const RolesPage = lazy(() => import("./pages/Roles/index"));
const DictTypesPage = lazy(() => import("./pages/Dicts/Types/index"));
const DictItemsPage = lazy(() => import("./pages/Dicts/Items/index"));
const ReviewLevelsPage = lazy(() => import("./pages/ReviewLevels/index"));
const ProfileInfoPage = lazy(() => import("./pages/Profile/Info/index"));
const ProfilePasswordPage = lazy(
  () => import("./pages/Profile/Password/index")
);
const DataManagementPage = lazy(() => import("./pages/DataManagement/index"));
const TeamsPage = lazy(() => import("./pages/Teams/index"));
const LawFirmsPage = lazy(() => import("./pages/LawFirms/index"));

const FullscreenLoading = () => (
  <div
    style={{
      width: "100%",
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Spin size="large" />
  </div>
);

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<FullscreenLoading />}>
            <LoginPage />
          </Suspense>
        }
      />

      <Route
        element={
          <Suspense fallback={<FullscreenLoading />}>
            <PageHeadLayout />
          </Suspense>
        }
      >
        <Route path="/" index element={<Navigate to="/cases" replace />} />
        <Route
          path="users"
          handle={{
            breadcrumb: "用户管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <UsersPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="cases">
          <Route
            index
            handle={{
              breadcrumb: "案件管理",
            }}
            element={
              <ProtectedRoute>
                <Suspense fallback={<FullscreenLoading />}>
                  <CasesPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            handle={{
              breadcrumb: "案件详情",
            }}
            element={
              <ProtectedRoute>
                <Suspense fallback={<FullscreenLoading />}>
                  <CaseDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="assign"
          handle={{
            breadcrumb: "案件分配管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <AssignmentsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="letters"
          handle={{
            breadcrumb: "所函申请",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <LettersPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="roles"
          handle={{
            breadcrumb: "角色管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <RolesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="reviewLevels"
          handle={{
            breadcrumb: "审级管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <ReviewLevelsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="teams"
          handle={{
            breadcrumb: "团队管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <TeamsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="lawFirms"
          handle={{
            breadcrumb: "律所管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <LawFirmsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="dictionaries">
          <Route
            index
            element={<Navigate to="/dictionaries/types" replace />}
          />
          <Route
            path="types"
            handle={{
              breadcrumb: "字典类型",
            }}
            element={
              <ProtectedRoute>
                <Suspense fallback={<FullscreenLoading />}>
                  <DictTypesPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="items/:typeId?"
            handle={{
              breadcrumb: "字典项",
            }}
            element={
              <ProtectedRoute>
                <Suspense fallback={<FullscreenLoading />}>
                  <DictItemsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="profile">
          <Route
            index
            path="info"
            handle={{
              breadcrumb: "个人信息",
            }}
            element={<ProfileInfoPage />}
          />
          <Route
            path="password"
            handle={{
              breadcrumb: "修改密码",
            }}
            element={<ProfilePasswordPage />}
          />
        </Route>
        {/* 隐藏的管理员数据管理页面 */}
        <Route
          path="admin/data"
          handle={{
            breadcrumb: "数据管理",
          }}
          element={
            <ProtectedRoute>
              <Suspense fallback={<FullscreenLoading />}>
                <DataManagementPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
